import React from 'react'
import { useGetBusinessID } from '@marketing-milk/frontend'
import {
  AdCreative,
  AdCreativeType,
  DraftCampaign,
  resolveOptimizationDefinition,
  SavedAudience,
} from '@marketing-milk/interfaces'
import { Check } from '@mui/icons-material'
import { Button } from '@mui/material'
import { CampaignDetails } from 'components'
import { DataTable } from 'components/DataTable'
import { flattenDeep, sortBy } from 'lodash/fp'
import {
  ErrorItem,
  ErrorsIndicator,
} from 'pages/business/campaigns/components/status-indicator/ErrorsIndicator'
import {
  getAdCreativeGroups,
  getAudiencesById,
  getRejectedCreatives,
  campaignDtoHasValidCreatives,
  campaignDtoHasValidSettings,
  getValidCreatives,
  describeUnavailableAudience,
  isAudienceAvailable,
  isValidCampaignDto,
} from 'pages/business/campaigns/helpers/campaignForm.helpers'
import { useHistory } from 'react-router-dom'
import { rejectNull, rejectUndefined } from '../../../../../app/util'
import { hasNoCreatives, isMissingAudiences } from '../../sprint.helpers'

interface Props {
  draftCampaigns?: DraftCampaign[]
  audiences?: SavedAudience[]
  sprintCreatives?: AdCreative[]
  sprintId?: number
  /**
   * The sprint's rejected ads on the review surface, for the route out of a client
   * rejection. Absent when the sprint has no packet — the rejection still reports,
   * just without the second route.
   */
  reviewHref?: string
  isLoadingData?: boolean
  openModal: (
    type: 'creatives' | 'audiences' | 'settings' | 'leadForms',
    draft: DraftCampaign
  ) => void
}
export const DraftCampaignsTable = ({
  draftCampaigns,
  audiences,
  sprintCreatives,
  sprintId,
  reviewHref,
  isLoadingData,
  ...props
}: Props) => {
  const businessId = useGetBusinessID()
  const history = useHistory()
  return (
    <DataTable<DraftCampaign>
      key={`draft-campaigns-${isLoadingData ? 'loading' : 'loaded'}`}
      hideColumnFilter
      hideSearch
      hidePagination
      recordsList={sortBy('id', draftCampaigns)}
      expansionPanel={{
        shouldExpand: () => true,
        render: draft => (
          <div className="flex items-center justify-center p-4 w-full">
            <CampaignDetails
              campaignDto={draft.config}
              selectedAudiences={getAudiencesById(draft.config.audienceIDs, audiences)}
              selectedCreatives={getAdCreativeGroups(
                draft.config.creativeGroups,
                sprintCreatives || []
              )}
              sprintId={sprintId}
              fromTab="draft-campaigns"
            />
          </div>
        ),
      }}
      tableSchema={{
        Name: {
          render: draft => draft.config.campaign.name,
        },
        'Edit Settings': {
          render: draft => (
            <Button
              variant="outlined"
              onClick={() => props.openModal('settings', draft)}
              className="whitespace-nowrap"
            >
              Settings
            </Button>
          ),
        },
        'Edit Adsets': {
          render: draft => (
            <Button
              variant="outlined"
              onClick={() => props.openModal('audiences', draft)}
              className="whitespace-nowrap"
            >
              Adsets
            </Button>
          ),
        },
        // "Choose" is what this column does — which ads are on the campaign — leaving
        // "Edit Ads" to mean changing an ad itself, on the review surface. The two used
        // to share a label on this same screen.
        'Choose Ads': {
          render: draft => (
            <Button
              variant="outlined"
              onClick={() => props.openModal('creatives', draft)}
              className="whitespace-nowrap"
            >
              Ads
            </Button>
          ),
        },
        'Edit Lead Forms': {
          render: draft =>
            resolveOptimizationDefinition(draft.config.campaign)?.requiresLeadCard ? (
              <Button
                variant="outlined"
                onClick={() => props.openModal('leadForms', draft)}
                className="whitespace-nowrap"
              >
                Lead Forms
              </Button>
            ) : null,
        },
        Status: {
          render: draft => {
            // Don't render status if data is still loading
            if (isLoadingData) {
              return <p className="text-gray-500 px-4 py-2">Loading...</p>
            }

            const unavailableAudiences = getAudiencesById(
              draft.config.audienceIDs,
              audiences
            ).filter(audience => !isAudienceAvailable(audience))

            const audienceErrors: ErrorItem[] = isMissingAudiences(draft.config)
              ? [
                  {
                    title: 'Missing Audiences',
                    message: 'You must attach audiences to this campaign',
                    details:
                      'If you have default campaigns configured, they will be automatically assigned to the campaigns you create in a sprint',
                    actionButton: (
                      <Button
                        variant={'contained'}
                        onClick={() => props.openModal('audiences', draft)}
                      >
                        Attach Audiences
                      </Button>
                    ),
                  },
                ]
              : unavailableAudiences.length > 0
              ? [
                  {
                    title: 'Unavailable Audiences',
                    message:
                      'Replace audiences that are unavailable in the connected ad account before publishing',
                    details: `${unavailableAudiences
                      .map(describeUnavailableAudience)
                      .join(
                        ', '
                      )} cannot be used with the connected ad account, so this campaign would target the wrong people.`,
                    actionButton: (
                      <Button
                        variant={'contained'}
                        onClick={() => props.openModal('audiences', draft)}
                      >
                        Fix Audiences
                      </Button>
                    ),
                  },
                ]
              : []

            const draftCreatives = rejectNull(
              rejectUndefined(
                flattenDeep(getAdCreativeGroups(draft.config.creativeGroups, sprintCreatives || []))
              )
            )
            const validCreatives = getValidCreatives(
              draft.config.creativeGroups,
              sprintCreatives || []
            )
            const invalidCreatives = draftCreatives?.filter(
              dc => !validCreatives.some(vc => vc.id === dc.id)
            )

            const invalidCreativeErrors = invalidCreatives.map(creative => ({
              title: `${creative?.name}`,
              message: 'Missing required properties',
              details:
                'Every creative must have media, name, website url, display url, description, and primary text.',
              actionButton: (
                <Button
                  variant={'contained'}
                  onClick={() => history.push(`/${businessId}/sprints/${creative?.id || ''}`)}
                >
                  Go to Creative
                </Button>
              ),
            }))

            const noCreativeError = {
              title: 'Missing Creatives',
              message: 'You must have creatives attached to your campaign',
              details:
                'Each campaign must have either an A and a B creative or a single dynamic creative.',
              actionButton: (
                <Button variant={'contained'} onClick={() => props.openModal('creatives', draft)}>
                  Add Creatives
                </Button>
              ),
            }

            // Read off the ads already resolved above rather than re-resolving the groups:
            // a rejection is a property of the ad, and the campaign holds it or it doesn't.
            const rejectedCreatives = getRejectedCreatives(
              draft.config.creativeGroups,
              sprintCreatives || []
            )

            // Both routes out, because either one resolves it: take the ad off the campaign,
            // or clear the rejection in review once the ad has been changed to answer it.
            const rejectedCreativeErrors: ErrorItem[] = rejectedCreatives.length
              ? [
                  {
                    title: 'Rejected By Client',
                    message: `${rejectedCreatives.length} ad${
                      rejectedCreatives.length === 1 ? '' : 's'
                    } on this campaign ${
                      rejectedCreatives.length === 1 ? 'was' : 'were'
                    } rejected by the client`,
                    details: `${rejectedCreatives
                      .map((creative: AdCreative) => creative.name)
                      .join(
                        ', '
                      )}. Remove them from the campaign, or clear the rejection in review once the ad answers it.`,
                    actionButton: (
                      <div className="flex gap-2">
                        <Button
                          variant={'contained'}
                          onClick={() => props.openModal('creatives', draft)}
                        >
                          Choose Ads
                        </Button>
                        {reviewHref && (
                          // A new tab, not a navigation: the AE is mid-edit on this sprint,
                          // and repairing an ad in review is a separate errand they come
                          // back from. The link lands on the packet's rejected ads rather
                          // than on one of them, since which to open first is their call.
                          <Button
                            variant={'outlined'}
                            href={reviewHref}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Edit Ads
                          </Button>
                        )}
                      </div>
                    ),
                  },
                ]
              : []

            // Meta builds a dynamic ad's combinations itself, so it is the whole ad group or it
            // cannot publish. The launcher has always refused this; until it was named here the
            // campaign read Ready beside a dead publish button.
            const dynamicAdsSharingAGroup = getAdCreativeGroups(
              draft.config.creativeGroups,
              sprintCreatives || []
            ).flatMap(group => {
              const ads = rejectUndefined(group)
              if (ads.length < 2) return []
              return ads.filter(ad => ad.format === AdCreativeType.dynamic)
            })

            const dynamicGroupingErrors: ErrorItem[] = dynamicAdsSharingAGroup.length
              ? [
                  {
                    title: 'Dynamic Ad Shares An Ad Group',
                    message: `${dynamicAdsSharingAGroup
                      .map(ad => ad.name)
                      .join(', ')} cannot run alongside other ads`,
                    details:
                      'Facebook assembles a dynamic ad from its own pool of media and copy, so it has to be the only ad in its group. Take it off this campaign, or give it a campaign of its own.',
                    actionButton: (
                      <Button
                        variant={'contained'}
                        onClick={() => props.openModal('creatives', draft)}
                      >
                        Choose Ads
                      </Button>
                    ),
                  },
                ]
              : []

            const creativeErrors: ErrorItem[] = [
              ...invalidCreativeErrors,
              ...rejectedCreativeErrors,
              ...dynamicGroupingErrors,
              ...(hasNoCreatives(draft.config) ? [noCreativeError] : []),
            ]

            const optimization = resolveOptimizationDefinition(draft.config.campaign)
            const missingOptimization = !optimization

            const campaignErrors: ErrorItem[] = [
              ...(missingOptimization
                ? [
                    {
                      title: 'Missing Campaign Optimization',
                      message:
                        'This campaign has no optimization selected, so it cannot be published.',
                      details:
                        'Both how Meta delivers the campaign and which creatives it can run follow from the objective and performance goal.',
                      actionButton: (
                        <Button
                          variant={'contained'}
                          onClick={() => props.openModal('settings', draft)}
                        >
                          Select Optimization
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]

            const missingLeadFormCreatives = optimization?.requiresLeadCard
              ? validCreatives.filter(creative => !creative.leadCardUsed)
              : []

            const leadFormErrors: ErrorItem[] = missingLeadFormCreatives.length
              ? [
                  {
                    title: 'Missing Lead Forms',
                    message: `${missingLeadFormCreatives.length} creative${
                      missingLeadFormCreatives.length === 1 ? '' : 's'
                    } ${
                      missingLeadFormCreatives.length === 1 ? 'has' : 'have'
                    } no lead form attached`,
                    details:
                      'This campaign collects leads on the ad, so every creative needs a lead form attached before it can publish.',
                    actionButton: (
                      <Button
                        variant={'contained'}
                        onClick={() => props.openModal('leadForms', draft)}
                      >
                        Assign Lead Forms
                      </Button>
                    ),
                  },
                ]
              : []

            const namedErrors = [
              ...campaignErrors,
              ...audienceErrors,
              ...creativeErrors,
              ...leadFormErrors,
            ]

            // The publish gate runs its own rules, and this list has drifted behind them before —
            // a campaign reading Ready beside a dead button is the worst of the two failures, so
            // anything the gate refuses without a named reason still reports as unpublishable,
            // and its button opens the part the gate actually refused.
            const [fixModal, fixLabel] = !campaignDtoHasValidSettings(draft.config)
              ? (['settings', 'Open Settings'] as const)
              : !campaignDtoHasValidCreatives(draft.config, sprintCreatives || [])
              ? (['creatives', 'Choose Ads'] as const)
              : (['audiences', 'Fix Audiences'] as const)
            const errors: ErrorItem[] =
              namedErrors.length > 0
                ? namedErrors
                : isValidCampaignDto(draft.config, sprintCreatives || [])
                ? []
                : [
                    {
                      title: 'Cannot Be Published',
                      message: 'This campaign is not ready to publish',
                      details:
                        'Check its budget, its dates, its audiences and its ads. Publishing is blocked until it is fixed.',
                      actionButton: (
                        <Button
                          variant={'contained'}
                          onClick={() => props.openModal(fixModal, draft)}
                        >
                          {fixLabel}
                        </Button>
                      ),
                    },
                  ]

            if (errors.length > 0)
              return (
                <ErrorsIndicator
                  errors={errors}
                  drawerTitle={`${draft.config.campaign.name} has errors`}
                  drawerDescription="If you attempted to upload this to facebook, it would not work correctly."
                />
              )

            return (
              <p className="bg-mm-success-100 text-mm-success-800 inline-flex max-w-max items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium">
                <Check fontSize="small" className="mr-1" />
                Ready
              </p>
            )
          },
        },
      }}
    />
  )
}
